/**
 * How each notification type looks (icon + accent colour) and which
 * settings group it belongs to — shared by the toast, the notifications
 * list/dropdown and Settings → Notifications. Mirrors the server catalogue
 * (api notification-catalog.ts); unknown types fall back to a bell.
 */
export type NotificationGroup =
  | 'messages' | 'social' | 'sales' | 'calendar' | 'visits'
  | 'team' | 'forms' | 'campaigns' | 'channels' | 'billing';

const CHANNEL: Record<string, { icon: string; color: string }> = {
  whatsapp: { icon: 'logo-whatsapp', color: '#25D366' },
  'facebook-messenger': { icon: 'logo-facebook', color: '#1877F2' },
  messenger: { icon: 'logo-facebook', color: '#1877F2' },
  'x-twitter': { icon: 'logo-twitter', color: '#1D9BF0' },
  phone: { icon: 'call', color: '#0EA5E9' },
};

const TYPE: Record<string, { icon: string; color: string; group: NotificationGroup }> = {
  new_message: { icon: 'chatbubble-ellipses', color: '#E39A2D', group: 'messages' },
  new_contact: { icon: 'person-add', color: '#E39A2D', group: 'messages' },
  conversation_expiring: { icon: 'hourglass', color: '#F59E0B', group: 'messages' },
  missed_call: { icon: 'call', color: '#EF4444', group: 'messages' },
  facebook_post_comment: { icon: 'chatbox', color: '#1877F2', group: 'social' },
  facebook_post_reaction: { icon: 'heart', color: '#E4405F', group: 'social' },
  new_order: { icon: 'receipt', color: '#10B981', group: 'sales' },
  quotation_status: { icon: 'document-text', color: '#6366F1', group: 'sales' },
  quotation_expiring: { icon: 'timer', color: '#F59E0B', group: 'sales' },
  low_stock: { icon: 'cube', color: '#F97316', group: 'sales' },
  calendar_reminder: { icon: 'alarm', color: '#8B5CF6', group: 'calendar' },
  new_booking: { icon: 'calendar', color: '#8B5CF6', group: 'calendar' },
  calendar_event_assigned: { icon: 'share-social', color: '#8B5CF6', group: 'calendar' },
  visit_assigned: { icon: 'walk', color: '#14B8A6', group: 'visits' },
  visit_sla_approaching: { icon: 'time', color: '#F59E0B', group: 'visits' },
  visit_sla_breached: { icon: 'alert-circle', color: '#EF4444', group: 'visits' },
  new_assignment: { icon: 'person', color: '#3B82F6', group: 'team' },
  assignment_update: { icon: 'swap-horizontal', color: '#64748B', group: 'team' },
  member_joined: { icon: 'people', color: '#3B82F6', group: 'team' },
  new_data_list_submission: { icon: 'clipboard', color: '#0D9488', group: 'forms' },
  campaign_finished: { icon: 'megaphone', color: '#25D366', group: 'campaigns' },
  task_finished: { icon: 'checkmark-done', color: '#64748B', group: 'campaigns' },
  channel_problem: { icon: 'warning', color: '#EF4444', group: 'channels' },
  template_status_update: { icon: 'document', color: '#25D366', group: 'channels' },
  whatsapp_quality_alert: { icon: 'shield', color: '#F59E0B', group: 'channels' },
  whatsapp_account_alert: { icon: 'shield', color: '#EF4444', group: 'channels' },
  subscription_reminder: { icon: 'card', color: '#E39A2D', group: 'billing' },
  subscription_expired: { icon: 'lock-closed', color: '#EF4444', group: 'billing' },
  payment_succeeded: { icon: 'checkmark-circle', color: '#10B981', group: 'billing' },
  payment_failed: { icon: 'close-circle', color: '#EF4444', group: 'billing' },
};

export const GROUP_ICON: Record<NotificationGroup, string> = {
  messages: 'chatbubbles-outline',
  social: 'thumbs-up-outline',
  sales: 'bag-handle-outline',
  calendar: 'calendar-outline',
  visits: 'walk-outline',
  team: 'people-outline',
  forms: 'clipboard-outline',
  campaigns: 'megaphone-outline',
  channels: 'radio-outline',
  billing: 'card-outline',
};

/** Icon + colour for a notification (messages take their channel's look). */
export function notificationVisual(type?: string, data?: Record<string, any> | null): { icon: string; color: string } {
  if ((type === 'new_message' || type === 'new_contact') && data?.channel && CHANNEL[data.channel]) return CHANNEL[data.channel];
  return TYPE[type || ''] || { icon: 'notifications', color: '#E39A2D' };
}

export const notificationGroupOf = (type?: string): NotificationGroup | null => TYPE[type || '']?.group || null;

/** Every notification type in a group (server-side list filter). */
export const typesInGroup = (group: NotificationGroup): string[] =>
  Object.entries(TYPE).filter(([, v]) => v.group === group).map(([k]) => k);

export const NOTIFICATION_GROUPS: NotificationGroup[] = ['messages', 'social', 'sales', 'calendar', 'visits', 'team', 'forms', 'campaigns', 'channels', 'billing'];
